import React, { useState, useEffect, useRef } from 'react';
import { Plus, Check, X } from 'lucide-react';
import { optionsAPI } from '../../services/api';

/**
 * ISP Name selector with ability to add new ISP profiles.
 * Profiles stored as plain strings under optionsKey `ispProfiles`.
 */
export default function ISPVlanSelect({ ispName, onChange, themeColor = 'var(--accent)' }) {
  const [profiles, setProfiles] = useState([]);
  const [adding,   setAdding]   = useState(false);
  const [newName,  setNewName]  = useState('');
  const [saving,   setSaving]   = useState(false);
  const nameRef = useRef(null);

  useEffect(() => {
    optionsAPI.get('ispProfiles')
      .then(({ data }) => {
        const parsed = (data.data || [])
          .map(s => String(s).split('|')[0].trim())
          .filter(Boolean);
        setProfiles([...new Set(parsed)]);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (adding && nameRef.current) nameRef.current.focus();
  }, [adding]);

  const handleSelect = (e) => {
    onChange({ ispName: e.target.value });
  };

  const saveNew = async () => {
    const n = newName.trim();
    if (!n) return;
    setSaving(true);
    try {
      const { data } = await optionsAPI.add('ispProfiles', n);
      const parsed = (data.data || [])
        .map(s => String(s).split('|')[0].trim())
        .filter(Boolean);
      setProfiles([...new Set(parsed)]);
      onChange({ ispName: n });
      setNewName('');
      setAdding(false);
    } catch {
      // keep open
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); saveNew(); }
    if (e.key === 'Escape') { setAdding(false); setNewName(''); }
  };

  return (
    <div>
      <label style={{ fontSize: '0.78rem', color: 'var(--c-6b7280)', display: 'block', marginBottom: 4 }}>ISP Name</label>
      <div style={{ display: 'flex', gap: 6 }}>
        <select
          className="form-control"
          value={ispName}
          onChange={handleSelect}
          style={{ flex: 1 }}
        >
          <option value="">Select ISP…</option>
          {profiles.map(p => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>
        <button
          type="button"
          title="Add new ISP"
          onClick={() => { setAdding(a => !a); setNewName(''); }}
          style={{
            flexShrink: 0, width: 36, height: 36,
            borderRadius: 8, border: `1.5px solid ${adding ? themeColor : '#e5e7eb'}`,
            background: adding ? `${themeColor}15` : 'var(--bg)',
            color: adding ? themeColor : '#6b7280',
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'all 0.15s',
          }}
        >
          <Plus size={16} strokeWidth={2} />
        </button>
      </div>

      {adding && (
        <div style={{
          marginTop: 8, padding: '10px 12px', borderRadius: 8,
          background: `${themeColor}08`,
          border: `1px dashed ${themeColor}50`,
        }}>
          <p style={{ fontSize: '0.75rem', color: 'var(--c-6b7280)', margin: '0 0 8px 0' }}>New ISP</p>
          <div style={{ display: 'flex', gap: 6 }}>
            <input
              ref={nameRef}
              value={newName}
              onChange={e => setNewName(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="ISP Name (e.g. BSNL)"
              style={{
                flex: 1, border: `1px solid ${themeColor}60`,
                borderRadius: 6, padding: '5px 8px', fontSize: '0.85rem', outline: 'none',
              }}
            />
            <button
              type="button"
              onClick={saveNew}
              disabled={saving || !newName.trim()}
              style={{
                padding: '5px 12px', borderRadius: 6, border: 'none',
                background: themeColor, color: 'var(--c-ffffff)', cursor: 'pointer',
                fontSize: '0.82rem', fontWeight: 600,
                opacity: !newName.trim() ? 0.5 : 1,
                display: 'flex', alignItems: 'center', gap: 4,
              }}
            >
              <Check size={13} /> {saving ? 'Saving…' : 'Add'}
            </button>
            <button
              type="button"
              onClick={() => { setAdding(false); setNewName(''); }}
              style={{
                padding: '5px 8px', borderRadius: 6,
                border: '1px solid var(--cb-e5e7eb)', background: 'var(--bg)',
                cursor: 'pointer', color: 'var(--c-6b7280)',
                display: 'flex', alignItems: 'center',
              }}
            >
              <X size={13} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
